import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductView } from '@lemoncloud/codes-goods-api';
import { X } from 'lucide-react';

import { useFetchChildProducts } from '@codes/product';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, Skeleton } from '@codes/ui-kit';

import { ProductCard } from './ProductCard';
import { ServiceEventLogDialog } from './ServiceEventLogDialog';
import { ServiceSettingsDialog } from './ServiceSettingsDialog';

interface ChildrenSheetProps {
    parentProduct: ProductView;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    isAutoDeploying: boolean;
    currentProductId?: string;
    onProductClick: (product: ProductView) => void;
    onSettingsClick: (productId: string) => void;
    onEventLogsClick: (productId: string) => void;
    onRedeploy: (productId: string) => void;
    onDelete: (productId: string) => void;
}

type NestedDialogState = {
    type: 'settings' | 'logs' | null;
    productId: string | null;
};

export const ChildrenSheet = ({
    parentProduct,
    open,
    onOpenChange,
    isAutoDeploying,
    currentProductId,
    onProductClick,
    onRedeploy,
    onDelete,
}: ChildrenSheetProps) => {
    const { t } = useTranslation();
    // Always fetch children when component is mounted (aistudio always has children)
    const { data: childProducts, isLoading } = useFetchChildProducts(parentProduct.id);

    const [nestedDialog, setNestedDialog] = useState<NestedDialogState>({
        type: null,
        productId: null,
    });

    const handleSettingsClick = (productId: string) => {
        setNestedDialog({ type: 'settings', productId });
    };

    const handleEventLogsClick = (productId: string) => {
        setNestedDialog({ type: 'logs', productId });
    };

    const closeNestedDialog = () => {
        setNestedDialog({ type: null, productId: null });
    };

    const handleSheetOpenChange = (open: boolean) => {
        if (!open) {
            setNestedDialog({ type: null, productId: null });
        }
        onOpenChange(open);
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleSheetOpenChange}>
                <SheetContent className="w-full max-w-[600px] overflow-y-auto" preventDismiss={false}>
                    <SheetHeader>
                        <SheetTitle>
                            {t('product.childDeployments', { count: childProducts?.data?.length || 0 })}
                        </SheetTitle>
                        <SheetClose>
                            <X className="h-5 w-5" />
                        </SheetClose>
                    </SheetHeader>

                    <div className="flex-1 py-6 space-y-4">
                        {isLoading && (
                            <>
                                {[...Array(2)].map((_, i) => (
                                    <div key={i} className="rounded-xl border bg-card shadow-sm p-5">
                                        <div className="flex justify-between items-center mb-4">
                                            <Skeleton className="h-6 w-20" />
                                            <Skeleton className="h-8 w-8 rounded" />
                                        </div>
                                        <div className="flex items-center gap-4 mb-4">
                                            <Skeleton className="h-[50px] w-[50px] rounded-[14px]" />
                                            <div className="flex-1 space-y-2">
                                                <Skeleton className="h-4 w-16" />
                                                <Skeleton className="h-5 w-2/3" />
                                            </div>
                                        </div>
                                        <Skeleton className="h-10 w-full mb-4" />
                                    </div>
                                ))}
                            </>
                        )}

                        {!isLoading && childProducts?.data && childProducts.data.length > 0 && (
                            <>
                                {childProducts.data.map(child => (
                                    <ProductCard
                                        key={child.id}
                                        product={child}
                                        isDeploying={isAutoDeploying && currentProductId === child.id}
                                        onProductClick={() => onProductClick(child)}
                                        onSettingsClick={() => handleSettingsClick(child.id)}
                                        onEventLogsClick={() => handleEventLogsClick(child.id)}
                                        onRedeploy={() => onRedeploy(child.id)}
                                        onDelete={() => onDelete(child.id)}
                                        isChild={true}
                                    />
                                ))}
                            </>
                        )}

                        {!isLoading && (!childProducts?.data || childProducts.data.length === 0) && (
                            <div className="flex flex-col items-center justify-center py-12 text-text-500">
                                <p>{t('product.noChildren')}</p>
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Nested Dialogs - Render outside Sheet with higher z-index */}
            {nestedDialog.type === 'settings' && nestedDialog.productId && (
                <ServiceSettingsDialog
                    open={true}
                    onOpenChange={open => !open && closeNestedDialog()}
                    productId={nestedDialog.productId}
                    nested={true}
                />
            )}

            {nestedDialog.type === 'logs' && nestedDialog.productId && (
                <ServiceEventLogDialog
                    productId={nestedDialog.productId}
                    open={true}
                    setOpen={open => !open && closeNestedDialog()}
                    nested={true}
                />
            )}
        </>
    );
};
